import { CheckCircle2 } from 'lucide-react'
import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useOnboarding } from '../../context/OnboardingContext'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../../theme/onboardingTheme'

const miscThirdPartyFiler = {
  name: 'MISC',
  primaryContact: 'Teri Blackshear',
  address: '',
  email: 'MISCFilings@morrisisc.com',
  phone: '720-502-3980',
}

function AgreementLine({
  label,
  value,
  wide = false,
}: {
  label: string
  value?: string
  wide?: boolean
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        gridColumn: wide ? { sm: 'span 2' } : undefined,
      }}
    >
      <Typography component="span" sx={{ flexShrink: 0, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
        {label}
      </Typography>
      <Box
        component="span"
        sx={{
          display: 'flex',
          minHeight: 32,
          flex: 1,
          alignItems: 'flex-end',
          borderBottom: '1px solid #64748b',
          backgroundColor: '#f8fafc',
          px: 1,
          pb: 0.5,
          fontSize: 14,
          color: '#0f172a',
        }}
      >
        {value}
      </Box>
    </Box>
  )
}

function AcknowledgementItem({
  number,
  title,
  children,
}: {
  number: number
  title: string
  children: string
}) {
  return (
    <Box
      component="li"
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { sm: '2rem 1fr' },
      }}
    >
      <Typography component="span" sx={{ textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
        {number}.
      </Typography>
      <Typography component="p">
        <strong>{title}</strong> - {children}
      </Typography>
    </Box>
  )
}

export function StepIdahoAgreement() {
  const { dispatch, advance } = useOnboarding()

  const acknowledgeAndContinue = () => {
    dispatch({ type: 'ACK_IDAHO_AGREEMENT' })
    advance()
  }

  return (
    <Stack spacing={2.5}>
      <Box sx={{ mx: 'auto', maxWidth: '64rem' }}>
        <Typography
          component="h2"
          sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.025em', color: '#0f172a' }}
        >
          Idaho Third Party Acknowledgement Agreement
        </Typography>
        <Typography component="p" sx={{ mt: 1, fontSize: 14, lineHeight: 1.625, color: '#475569' }}>
          Please review and confirm the accuracy of the below Idaho Third Party Acknowledgement
          Agreement Form, which authorizes MISC to file on behalf of the licensed{' '}
          <strong>INDIVIDUAL</strong>. This form is required by Idaho.
        </Typography>
      </Box>

      <Paper
        component="section"
        elevation={0}
        sx={{
          mx: 'auto',
          maxWidth: '64rem',
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          p: { xs: 3, sm: 4 },
          color: '#0f172a',
          boxShadow: 1,
        }}
      >
        <Stack spacing={3}>
          <Box component="header" sx={{ textAlign: 'center' }}>
            <Stack spacing={2}>
              <Box sx={{ textAlign: 'left' }}>
                <Typography
                  sx={{
                    fontSize: { xs: '3rem', sm: '3.75rem' },
                    fontWeight: 900,
                    lineHeight: 1,
                    letterSpacing: '-0.025em',
                    color: '#14532d',
                  }}
                >
                  SLA
                  <Box
                    component="span"
                    sx={{ ml: 0.5, fontSize: { xs: '2.25rem', sm: '3rem' } }}
                  >
                    IDAHO
                  </Box>
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.2em',
                    color: '#4d7c0f',
                  }}
                >
                  Surplus Line Association of Idaho
                </Typography>
              </Box>
              <Typography
                component="h3"
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 700, color: '#14532d' }}
              >
                Third-Party Filer Acknowledgment Agreement
              </Typography>
              <Typography
                component="p"
                sx={{
                  mx: 'auto',
                  maxWidth: '56rem',
                  fontSize: 14,
                  fontStyle: 'italic',
                  lineHeight: 1.625,
                  color: '#475569',
                }}
              >
                The Surplus Line Association of Idaho does not approve, endorse, or recommend any
                third-party filing service. SLA Idaho does not enter into special arrangements,
                provide considerations, or make agreements regarding the activities of such third
                parties.
              </Typography>
            </Stack>
          </Box>

          <Typography component="p" sx={{ fontSize: 14, lineHeight: 1.625 }}>
            This Agreement is between the undersigned Third-Party Filer and the Surplus Line
            Association of Idaho (SLA Idaho), solely to acknowledge the terms under which access may
            be granted to the Idaho Surplus Line Broker Portal for any licensed Surplus Line Broker
            that designates the filer as an authorized third-party.
          </Typography>

          <Stack spacing={1.5}>
            <Typography component="p" sx={{ fontWeight: 700 }}>
              Third-Party Filer Information
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { sm: '1fr 1fr' } }}>
              <AgreementLine label="Third-Party Filer Name:" value={miscThirdPartyFiler.name} wide />
              <AgreementLine
                label="Primary Contact Name:"
                value={miscThirdPartyFiler.primaryContact}
                wide
              />
              <AgreementLine
                label="Third-Party Filer Address:"
                value={miscThirdPartyFiler.address}
                wide
              />
              <AgreementLine
                label="Third-Party Filer Email:"
                value={miscThirdPartyFiler.email}
                wide
              />
              <AgreementLine
                label="Third-Party Filer Phone:"
                value={miscThirdPartyFiler.phone}
                wide
              />
            </Box>
          </Stack>

          <Stack spacing={1.5}>
            <Typography component="p" sx={{ fontWeight: 700 }}>
              Acknowledgments
            </Typography>
            <Box
              component="ol"
              sx={{ display: 'flex', flexDirection: 'column', gap: 1, fontSize: 14, lineHeight: 1.625, m: 0, p: 0, listStyle: 'none' }}
            >
              <AcknowledgementItem number={1} title="Limited Role">
                I acknowledge that I may be authorized by one or more Surplus Line Brokers, through a
                Broker Authorization form on file with SLA Idaho, to act only as a filer in the Idaho
                Broker Portal. I have no authority to transact insurance, bind coverage, or act as a
                surplus line broker in the State of Idaho.
              </AcknowledgementItem>
              <AcknowledgementItem number={2} title="Compliance">
                I will comply with all applicable laws, regulations and SLA Idaho procedures when
                using the Portal.
              </AcknowledgementItem>
              <AcknowledgementItem number={3} title="Confidentiality">
                I will keep all information accessed through the Portal confidential and use it only
                to make filings for the authorizing broker.
              </AcknowledgementItem>
              <AcknowledgementItem number={4} title="Portal Use">
                The Idaho Broker Portal, including its systems, software, and documentation, is the
                exclusive property of SLA Idaho. Access is provided only to file surplus line
                transactions on behalf of licensed brokers who have authorized me through a Broker
                Authorization form on file with SLA Idaho. The Portal and its materials may not be
                copied, shared, reverse engineered, or used for any purpose other than making
                authorized filings.
              </AcknowledgementItem>
              <AcknowledgementItem number={5} title="Indemnification">
                I agree to indemnify and hold harmless SLA Idaho, its officers, employees, and agents
                from any claims, damages, or expenses (including attorneys' fees) arising out of my
                actions, omissions, or misuse of the Portal.
              </AcknowledgementItem>
              <AcknowledgementItem number={6} title="Revocation">
                My authorization may be revoked at any time by any authorizing broker or by SLA
                Idaho, at their discretion.
              </AcknowledgementItem>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              pt: 1,
              gridTemplateColumns: { sm: '1fr 10rem' },
            }}
          >
            <AgreementLine label="Signature of Third-Party Filer:" />
            <AgreementLine label="Date:" />
            <AgreementLine label="Printed Name of Third-Party Filer:" value={miscThirdPartyFiler.primaryContact} wide />
          </Box>

          <Typography component="p" sx={{ fontSize: 12, color: '#64748b' }}>
            Third Party Filer Acknowledgment Agreement 09/2025
          </Typography>
        </Stack>
      </Paper>

      <Box
        sx={{
          mx: 'auto',
          display: 'flex',
          maxWidth: '64rem',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: '12px',
          border: '1px solid #bfdbfe',
          backgroundColor: '#eff6ff',
          px: 2,
          py: 2,
          textAlign: 'center',
        }}
      >
        <Button
          type="button"
          onClick={acknowledgeAndContinue}
          startIcon={<CheckCircle2 size={16} />}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            borderRadius: '8px',
            backgroundColor: MISC_PRIMARY,
            px: 2.5,
            py: 1.25,
            fontSize: 14,
            fontWeight: 600,
            color: '#ffffff',
            boxShadow: 1,
            textTransform: 'none',
            '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
          }}
        >
          I confirm this Idaho agreement is accurate
        </Button>
      </Box>
    </Stack>
  )
}
